import React from 'react';

export interface ValueAndOption {
  value: number | string;
  option: string;
}

interface SelectInputProps {
  label: string;
  value: number | string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  disabled?: boolean;
  width?: string;
  valuesAndOptions: ValueAndOption[];
  custom_test_id?: string;
  multiple?: boolean;
  size?: number;
}

export const SelectInput: React.FC<SelectInputProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  width,
  valuesAndOptions,
  custom_test_id,
  multiple = false,
  size,
}) => {
  const handleMultipleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (onChange && multiple) {
      const selectedValues = Array.from(
        e.target.selectedOptions,
        (option) => option.value,
      );
      const commaSeparatedValue = selectedValues.join(',');
      // Create a synthetic event with the comma-separated value
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: commaSeparatedValue,
        },
      } as React.ChangeEvent<HTMLSelectElement>;
      onChange(syntheticEvent);
    } else if (onChange) {
      onChange(e);
    }
  };

  // For multiple select, parse the comma-separated value string into an array
  const selectedValuesArray =
    multiple && typeof value === 'string'
      ? value
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v !== '')
      : [];

  // For multiple selects, use minimal styling to allow size attribute to work
  // Avoid using 'select' or 'input' classes that may constrain height
  const selectClassName = multiple
    ? 'block w-full border border-primary rounded-md p-1 bg-base-100 focus:outline-none focus:ring-2 focus:ring-primary'
    : 'input input-bordered input-primary block w-full';

  // Calculate the size value for multiple selects
  const selectSize = multiple
    ? (size ?? Math.min(4, valuesAndOptions.length))
    : undefined;

  // For multiple selects, add inline style to override any height constraints from CSS
  // This allows the size attribute to control the visible height
  const selectStyle = multiple
    ? { height: 'auto', maxHeight: 'none', minHeight: 'auto' }
    : {};

  return (
    <div key={'select-input-div_' + label} className={'form-control ' + width}>
      <label>
        <div className="label">
          <span className="label-text">{label}</span>
        </div>
        <select
          key={'select-input_' + label}
          className={selectClassName}
          style={selectStyle}
          value={multiple ? selectedValuesArray : value}
          onChange={handleMultipleChange}
          disabled={disabled}
          data-testid={custom_test_id}
          multiple={multiple}
          size={selectSize}
        >
          {valuesAndOptions.map(
            ({ value: optionValue, option }: ValueAndOption) => {
              return (
                <option key={optionValue} value={optionValue}>
                  {option}
                </option>
              );
            },
          )}
        </select>
      </label>
    </div>
  );
};
